import { useCallback } from 'react';

import type { DomainMatch } from '../types';

// Domain-match fetcher used by EmailOtpStep after the user verifies their
// email and by SelfServiceSMBSignupModal on resume.
//
// Identity-derived query: the server resolver (groupsByEmailDomain) reads
// the email domain from the authenticated user record (Bearer auth from
// the post-signup session). The webapp does not thread the email to the
// server, which is also why there is no last-wins guard — the only way
// the call can produce different results is if the authenticated user's
// identity changes, and the surrounding modal flow does not do that
// mid-session.
//
// The email arg on fetchMatches is a client-side state guard (don't fire
// the query before the email is set), not a server input.
//
// API plan Decision 22 relaxed the original Decision 2 (OTP-gated) so the
// server-side resolver accepts either auth mode; the webapp drops the
// OTP-token header for the self-service path.

export interface DomainMatchQueryArgs {
  email: string;
}

export type DomainMatchRunQuery = () => Promise<DomainMatch[]>;

export interface UseDomainMatchOptions {
  runQuery: DomainMatchRunQuery;
}

export interface UseDomainMatchResult {
  fetchMatches: (args: DomainMatchQueryArgs) => Promise<DomainMatch[]>;
}

export function useDomainMatch({ runQuery }: UseDomainMatchOptions): UseDomainMatchResult {
  const fetchMatches = useCallback(
    async (args: DomainMatchQueryArgs) => {
      const trimmed = args.email.trim();
      if (!trimmed) {
        throw new Error('email is required to fetch domain matches');
      }
      return runQuery();
    },
    [runQuery]
  );

  return { fetchMatches };
}
